import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { fetchChallengesWithStatus } from 'services/challenge.service';
import { buscarIniciativasFavoritas } from 'services/iniciativa.service';
import ilustracaoHero from 'assets/img/2026/naves_home.png';
import { Header } from '../HomePublic/components/Header';
import { ProgressoGeral } from './components/ProgressoGeral';
import { TarefaCard } from './components/TarefaCard';
import { BadgeItem } from './components/BadgeItem';
import { IniciativaCard } from '../HomePublic/components/IniciativaCard';
import './style.scss';

export function MinhaJornada() {
  const { user } = useAuth();
  const location = useLocation();
  const [tarefas, setTarefas] = useState<any[]>([]);
  const [badges, setBadges] = useState<any[]>([]);
  const [iniciativasFavoritas, setIniciativasFavoritas] = useState<any[]>([]);
  const [loadingChallenges, setLoadingChallenges] = useState(true);
  const [loadingFavoritas, setLoadingFavoritas] = useState(true);

  useEffect(() => {
    async function loadChallenges() {
      if (!user?._id) return;
      
      try {
        setLoadingChallenges(true);
        const challengesData = await fetchChallengesWithStatus(user._id);
        
        const sortedChallenges = [...challengesData].sort((a, b) => {
          if (a.concluida && !b.concluida) return -1;
          if (!a.concluida && b.concluida) return 1;
          return 0;
        });
        
        setTarefas(sortedChallenges);
        
        const sortedBadges = sortedChallenges.map(challenge => ({
          id: challenge.id,
          nome: challenge.titulo,
          imagem: challenge.icone,
          conquistado: challenge.concluida
        }));
        
        setBadges(sortedBadges);
      } catch (error) {
        console.error('Erro ao carregar desafios:', error);
      } finally {
        setLoadingChallenges(false);
      }
    }
    
    loadChallenges();
  }, [user?._id]);

  useEffect(() => {
    async function loadFavoritas() {
      if (!user?._id) return;
      
      try {
        setLoadingFavoritas(true);
        const favoritas = await buscarIniciativasFavoritas(user._id);
        setIniciativasFavoritas(favoritas);
      } catch (error) {
        setIniciativasFavoritas([]);
      } finally {
        setLoadingFavoritas(false);
      }
    }
    
    loadFavoritas();
  }, [user?._id]);

  // Scroll para badges quando carregamento concluir e hash estiver presente
  useEffect(() => {
    if (!loadingChallenges && location.hash === '#badges') {
      setTimeout(() => {
        const badgesSection = document.querySelector('.colecao-badges');
        if (badgesSection) {
          const headerOffset = 80;
          const elementPosition = badgesSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 300);
    } else if (!loadingChallenges && !location.hash) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [loadingChallenges, location.hash]);

  if (!user) return null;

  const tarefasConcluidas = tarefas.filter(t => t.concluida).length;
  const totalTarefas = tarefas.length;
  const percentualProgresso = totalTarefas > 0 ? Math.round((tarefasConcluidas / totalTarefas) * 100) : 0;

  return (
    <div id="minha-jornada-wrapper">
      <Header 
        currentPage="minha-jornada"
      />

      <section className="hero-jornada">
        <div className="hero-jornada__content">
          <div className="hero-jornada__text">
            <h1 className="hero-jornada__title">
              Minha Jornada no <span className="hero-jornada__highlight">Prêmio BB 2026</span>
            </h1>
            <p className="hero-jornada__subtitle">
              Complete desafios e desbloqueie<br />
              badges exclusivos
            </p>
          </div>
          <div className="hero-jornada__illustration">
            <img 
              src={ilustracaoHero} 
              alt="Ilustração espacial"
              className="hero-jornada__image"
            />
          </div>
        </div>
      </section>

      <main className="jornada-main">
        <div className="jornada-main__container">
          <ProgressoGeral 
            tarefasConcluidas={tarefasConcluidas}
            totalTarefas={totalTarefas}
            percentual={percentualProgresso}
          />

          <section className="lista-tarefas">
            <h2 className="lista-tarefas__title">Lista de tarefas</h2>
            {loadingChallenges ? (
              <div className="flex justify-center items-center py-8">
                <span className="text-blue-bb">Carregando tarefas...</span>
              </div>
            ) : (
              <div className="lista-tarefas__wrapper">
                <div className="lista-tarefas__grid">
                  {tarefas.map((tarefa) => (
                    <TarefaCard 
                      key={tarefa.id}
                      titulo={tarefa.titulo}
                      descricao={tarefa.descricao}
                      concluida={tarefa.concluida}
                      icone={tarefa.icone}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="colecao-badges">
            <h2 className="colecao-badges__title">Coleção de badges</h2>
            {loadingChallenges ? (
              <div className="flex justify-center items-center py-8">
                <span className="text-blue-bb">Carregando badges...</span>
              </div>
            ) : (
              <div className="colecao-badges__wrapper">
                <div className="colecao-badges__grid">
                  {badges.map((badge) => (
                    <BadgeItem 
                      key={badge.id}
                      nome={badge.nome}
                      imagem={badge.imagem}
                      conquistado={badge.conquistado}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="iniciativas-favoritas">
            <h2 className="iniciativas-favoritas__title">Suas iniciativas favoritas</h2>
            {loadingFavoritas ? (
              <div className="flex justify-center items-center py-8">
                <span className="text-blue-bb">Carregando iniciativas favoritas...</span>
              </div>
            ) : iniciativasFavoritas.length > 0 ? (
              <>
                <div className="iniciativas-favoritas__grid">
                  {iniciativasFavoritas.slice(0, 3).map((iniciativa) => (
                    <IniciativaCard
                      key={iniciativa.id}
                      {...iniciativa}
                    />
                  ))}
                </div>
                {iniciativasFavoritas.length > 3 && (
                  <button className="iniciativas-favoritas__ver-mais">VER MAIS</button>
                )}
              </>
            ) : (
              <div className="iniciativas-favoritas__empty">
                <p>Você ainda não favoritou nenhuma iniciativa.</p>
                <p>Explore as iniciativas e salve suas favoritas!</p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
